import { Component, DestroyRef, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { ActivatedRoute, Router } from "@angular/router";
import { IdService } from "../../services/id/id.service";
import { PermissionsService } from "../../services/permissions/permissions.service";
import { SharedService } from "../../services/shared/shared.service";
import { AssetsService } from "../../services/assets/assets.service";
import { TranslateService } from "@xref/ngx";
import Constants from "../../shared/constants";
import * as Cookies from 'js-cookie';
import { environment } from "../../../environments/environment";

interface DataRegion {
    key: string;
    value: string;
}

@Component({
    selector: 'xlog-user-details',
    templateUrl: './user-details.component.pug',
    styleUrls: ['./user-details.component.scss'],
    standalone: false
})
export class UserDetailsComponent implements OnInit {
    @ViewChild("userDetail") userDetail: any;
    @Input() token: string;
    compWindow: any;
    email: string;
    moment: any;
    // TODO: Remove first and last name + refs during FF cleanup for ff_ent_10781_sign_up
    textValueFirstName = '';
    textValueLastName = '';
    textValuePassword = '';
    textValueConfirmPassword = '';
    dataRegions: DataRegion[] = [];
    inputType = 'password';
    user_model = {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        token: '',
        assigned_org_uuid: ''
    };
    org_model = {
        orgIndustry: '',
        dataRegion: '',
        orgName: '',
        orgSize: ''
    };
    acceptedChars = "!\"#$%&'()*+,-./:;<=>?@[]^_`{|}~";

    isLengthValid = false;
    hasUpperCase = false;
    hasLowerCase = false;
    hasSpecialChar = false;
    hasNumber = false;
    ent_9105_penetration_test = false;
    ff_ent_10781_sign_up = false;
    loaded = true;
    showBanner = false;
    preloadedImageEyeClosed: any;
    preloadedImageEyeOpen: any;
    preloadedImageDanger: any;
    redirectionFromUserLoggedInPage = false;

    protected _translatedIndustries: any[] = [];
    protected _translatedDataRegions: any[] = [];
    protected readonly orgSizes = Constants.ORG_SIZES;

    constructor(
        public modalRef: BsModalRef,
        private modalService: BsModalService,
        private route: ActivatedRoute,
        private router: Router,
        private idService: IdService,
        private featureFlagService: PermissionsService,
        private sharedService: SharedService,
        private assetsService: AssetsService,
        private translateService: TranslateService,
        private destroyRef: DestroyRef
    ) {
    }

    get isContinueDisabled(): boolean {
        if (this.ff_ent_10781_sign_up) {
            return !(
                this.isLengthValid &&
                this.hasUpperCase &&
                this.hasLowerCase &&
                this.hasNumber &&
                this.hasSpecialChar &&
                this.user_model.password === this.textValueConfirmPassword &&
                !!this.org_model.orgIndustry &&
                !!this.org_model.dataRegion
            );
        } else {
            return !(
                this.isLengthValid &&
                this.hasUpperCase &&
                this.hasLowerCase &&
                this.hasNumber &&
                this.hasSpecialChar &&
                this.textValuePassword === this.textValueConfirmPassword &&
                this.textValueFirstName.trim() !== '' &&
                this.textValueLastName.trim() !== ''
            );
        }
    }

    ngOnInit() {
        this.preloadImage();
        this.ent_9105_penetration_test = this.featureFlagService.isFeatureActivated('ENT-9105-PENETRATION-TEST-2025');
        this.ff_ent_10781_sign_up = this.featureFlagService.isFeatureActivated('ent-10781-signup-improvements');
        this.compWindow = window;

        if (this.ff_ent_10781_sign_up) {
            this.updateTranslatedIndustries();
            this.updateTranslatedDataRegions();
        }

        this.moment = moment;
        this.route.params.subscribe(params => {
            this.email = params['email'];
            this.user_model.email = params['email'];
        });
        this.route.queryParams.subscribe(queryParams => {
            this.token = queryParams['t'];
            this.user_model.token = queryParams['t'];
        });
        if (this.ff_ent_10781_sign_up && this.token) {
            this.fetchSignupOrgDetails();
        }
    }

    showAlertBanner() {
        this.showBanner = true;
        setTimeout(() => {
            this.showBanner = false;
        }, 5000);  // Hide after 5 seconds
    }

    async preloadImage() {
        this.preloadedImageEyeClosed = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.EYE_CLOSED_ICON);
        this.preloadedImageEyeOpen = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.EYE_OPEN_ICON);
        this.preloadedImageDanger = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.ICON_DANGER);
    }

    closeModal() {
        this.modalRef.hide();
    }

    redirectionTermsAndCondition() {
        window.open(Constants.SIGNUP_TERMS_XREF_URL, "_blank");
    }

    toggleShowPassword() {
        this.inputType = this.inputType === 'text' ? 'password' : 'text';
    }

    validatePassword() {
        const password = this.textValuePassword;

        this.isLengthValid = this.ent_9105_penetration_test ? password.length >= 12 : password.length >= 8;
        this.hasUpperCase = /[A-Z]/.test(password);
        this.hasLowerCase = /[a-z]/.test(password);
        this.hasSpecialChar = /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(password);
        this.hasNumber = /[0-9]/.test(password);
        this.user_model.password = password;
    }

    dropDownOrgIndustryOptionSelected(orgIndustry: { key: number }): void {
        this.org_model.orgIndustry = Constants.EN_ORG_INDUSTRIES?.[orgIndustry?.key] ?? null;
    }

    dropDownDataRegionOptionSelected(dataRegion: DataRegion): void {
        this.org_model.dataRegion = dataRegion?.key || null;
    }

    onSubmit() {
        if (this.ff_ent_10781_sign_up) {
            if (this.isContinueDisabled) {
                return;
            }
            this.loaded = false;
            this.user_model.password = this.textValuePassword;
            localStorage.setItem('previousUrl', '/user-details');
            this.idService.logUser(this.token, {
                firstName: this.user_model.first_name,
                lastName: this.user_model.last_name,
            }).subscribe((response) => {
                this.loaded = true;
                this.setupNewUserOrg();
            }, (error) => {
                this.loaded = true;
                console.log(error);
                this.router.navigate(['signup/expired']);
            });
        } else {
            this.onSubmitDeprecated();
        }
    }

    onSubmitDeprecated() {
        if (this.isContinueDisabled) {
            return;
        }
        this.loaded = false;
        const userDetails = {
            first_name: this.textValueFirstName,
            last_name: this.textValueLastName,
            email: this.email,
            password: this.textValuePassword,
            token: this.token,
            previousUrl: '/user-details'
        };
        localStorage.setItem('previousUrl', '/user-details');
        this.idService.logUser(this.token, {
            firstName: this.textValueFirstName,
            lastName: this.textValueLastName,
        }).subscribe((response) => {
            this.loaded = true;
            this.sharedService.setUserData(userDetails);
            this.router.navigate(['/org-creation']);
        }, (error) => {
            this.loaded = true;
            console.log(error);
            this.router.navigate(['signup/expired']);
        });
    }

    setupNewUserOrg(): void {
        if (!this.org_model.orgName || this.org_model.orgIndustry === '' || this.org_model.dataRegion === '' || this.org_model.orgSize === '') {
            return;
        }
        this.loaded = false;
        const locale = Cookies.get(Constants.LOCALE_TOKEN) || 'en-au';
        let urlLocale = locale;
        const omniLocales = Constants.OMNI_LOCALES;
        if (!omniLocales.includes(urlLocale)) {
            urlLocale = 'en-au';
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const orgDetails = {
            name: this.org_model.orgName,
            industry: this.org_model.orgIndustry,
            org_size: this.org_model.orgSize,
            region: this.org_model.dataRegion,
            email: this.user_model.email,
            language_locale: locale,
            timezone
        };

        this.idService.createOrganisation(this.user_model?.token, orgDetails).subscribe({
            next: (orgResponse) => {
                this.user_model.assigned_org_uuid = orgResponse.data.global_org_uuid;
                this.idService.createUser(this.user_model.token, this.user_model).subscribe({
                    next: () => {
                        const tokenData = {
                            email: this.user_model.email,
                            password: this.user_model.password,
                            organisation_uuid: this.user_model.assigned_org_uuid
                        };
                        if (this.redirectionFromUserLoggedInPage) {
                            delete tokenData.password;
                        }
                        this.idService.getJwtAccessToken(tokenData).subscribe({
                            next: (tokenResponse) => {
                                this.loaded = true;
                                this.idService.removeToken();
                                // @ts-ignore
                                this.idService.setTokens(tokenResponse.data);
                                Cookies.set('xref-first-time-login', 'true', { domain: environment.DOMAIN });
                                Cookies.set('xref-onboarding-video-guides', 'true', { domain: environment.DOMAIN });
                                // @ts-ignore
                                this.compWindow.location.href = environment.OMNI_URL + urlLocale.toUpperCase();
                            },
                            error: () => {
                                this.showAlertBanner();
                                this.loaded = true;
                            }
                        });
                    },
                    error: (err) => {
                        console.error('User creation failed:', err);
                        this.loaded = true;
                        this.showAlertBanner();
                    }
                });
            },
            error: (error) => {
                if (error?.error?.data?.global_org_uuid) {
                    const deleteOrgData = {
                        name: this.org_model.orgName,
                        region: this.org_model.dataRegion,
                        global_org_uuid: error?.error?.data?.global_org_uuid
                    };

                    this.idService.rollBackOrgAndUser(this.user_model?.token, deleteOrgData).subscribe({
                        next: () => {
                            this.showAlertBanner();
                            this.loaded = true;
                            console.log('Rollback org successful');
                        },
                        error: (rollbackError) => {
                            this.showAlertBanner();
                            this.loaded = true;
                            console.error('Rollback org failed:', rollbackError);
                        }
                    });
                } else if (error.status === 401) {
                    window.location.href = '/signup/expired';
                    localStorage.removeItem("userData");
                } else {
                    console.error('Organisation creation failed:', error);
                    this.loaded = true;
                    this.showAlertBanner();
                }
            }
        });
    }

    private fetchSignupOrgDetails(): void {
        this.idService.getSignupOrganisationDetails(this.token).subscribe({
            next: (response) => {
                const data = response?.data || {};
                this.user_model.first_name = data.first_name || '';
                this.user_model.last_name = data.last_name || '';
                this.org_model.orgName = data.org_name || '';
                this.org_model.orgSize = data.org_size || '';
                this.org_model.orgIndustry = data.industry || '';
                this.org_model.dataRegion = data.region || '';
            },
            error: (err) => {
                console.error('Failed to fetch signup organisation details:', err);
            }
        });
    }

    private initializeDataRegions(): void {
        const currentUrl = window.location.origin;

        if (currentUrl.includes('dev.xref.com') || currentUrl.includes('localhost')) {
            // Dev environment - only Australia
            this.dataRegions = this._translatedDataRegions.filter(region => region.key === 'au');
        } else if (currentUrl.includes('sandbox.xref.com')) {
            // Sandbox environment - Australia and Europe
            this.dataRegions = this._translatedDataRegions.filter(region =>
                ['au', 'eu'].includes(region.key)
            );
        } else if (currentUrl.includes('xref.com')) {
            // Production environment - all regions
            this.dataRegions = [...this._translatedDataRegions];
        } else {
            // Default fallback - only Australia
            console.warn('Unknown environment detected, defaulting to Australia only');
            this.dataRegions = this._translatedDataRegions.filter(region => region.key === 'au');
        }
    }

    private updateTranslatedIndustries() {
        const keys = Constants.ORG_INDUSTRIES.map(i => i.value);
        this.translateService.get(keys)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(translations => {
                this._translatedIndustries = Constants.ORG_INDUSTRIES
                    .map(industry => ({
                        key: industry.key,
                        value: translations[industry.value]
                    }))
                    .sort((a, b) => a.value.localeCompare(b.value));
            });
    }

    private updateTranslatedDataRegions() {
        const keys = Constants.DATA_REGIONS.map(r => r.value);
        this.translateService.get(keys)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(translations => {
                this._translatedDataRegions = Constants.DATA_REGIONS.map(region => ({
                    key: region.key,
                    value: translations[region.value]
                }));
                this.initializeDataRegions();
            });
    }
}
